// src/components/Staking/StakingPositions.js

import React, { useContext, useEffect, useState } from 'react';
import { StakingContext } from '../../context/StakingContext';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
  Box,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import dayjs from 'dayjs';
import { NETWORK_CONFIG } from '../../contracts/constants';

const formatNumber = (num) => {
  const number = parseFloat(num);
  if (isNaN(number)) return '0';
  if (number >= 1e12) {
    return (number / 1e12).toFixed(1) + 'T';
  } else if (number >= 1e9) {
    return (number / 1e9).toFixed(1) + 'B';
  } else if (number >= 1e6) {
    return (number / 1e6).toFixed(1) + 'M';
  } else if (number >= 1e3) {
    return (number / 1e3).toFixed(1) + 'K';
  } else {
    return number.toFixed(1);
  }
};

const StakingPositions = () => {
  const {
    stakingPositions,
    unstakeTokens,
    claimReward,
    restakeTokens,
    fetchStakingPositions,
    loadingStake,
    loadingUnstake,
  } = useContext(StakingContext);

  const [expandedPositionId, setExpandedPositionId] = useState(null);
  const [openClaimDialog, setOpenClaimDialog] = useState(false);
  const [selectedPositionId, setSelectedPositionId] = useState(null);
  const [newStakingPeriod, setNewStakingPeriod] = useState('');
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [isRestaking, setIsRestaking] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const stakingPeriodLabels = ['1wk', '3wks', '6wks', '49wks'];

  useEffect(() => {
    fetchStakingPositions();
    const interval = setInterval(fetchStakingPositions, 60000);
    return () => clearInterval(interval);
  }, [fetchStakingPositions]);

  const toggleExpandPosition = (positionId) => {
    setExpandedPositionId(expandedPositionId === positionId ? null : positionId);
  };

  const handleClaimRewardClick = (positionId) => {
    setSelectedPositionId(positionId);
    setOpenClaimDialog(true);
  };

  const handleClaimAndAction = async (action) => {
    try {
      setIsProcessingAction(true);

      if (action === 'restake' && newStakingPeriod === '') {
        setSnackbar({
          open: true,
          message: 'Please select a new staking period.',
          severity: 'warning',
        });
        return;
      }

      if (action === 'restake') {
        setIsRestaking(true);
        await restakeTokens(selectedPositionId, newStakingPeriod);
        setSnackbar({
          open: true,
          message: 'Successfully claimed rewards and restaked your tokens!',
          severity: 'success',
        });
      } else if (action === 'unstake') {
        await claimReward(selectedPositionId);
        await unstakeTokens(selectedPositionId);
        setSnackbar({
          open: true,
          message: 'Successfully claimed rewards and unstaked your tokens!',
          severity: 'success',
        });
      }

      await fetchStakingPositions();
    } catch (error) {
      if (error.code === 4001 || 
          error.code === 'ACTION_REJECTED' ||
          (error.message && error.message.toLowerCase().includes('user rejected')) ||
          (error.message && error.message.toLowerCase().includes('user denied')) ||
          (error.data && error.data.code === 'ACTION_REJECTED')) {
        return;
      } else {
        console.error('Action failed:', error);
        setSnackbar({
          open: true,
          message: 'Failed to complete action. Please try again.',
          severity: 'error',
        });
      }
    } finally {
      setIsProcessingAction(false);
      setIsRestaking(false);
      setOpenClaimDialog(false);
      setSelectedPositionId(null);
      setNewStakingPeriod('');
    }
  };

  const handleCancelClaim = () => {
    setOpenClaimDialog(false);
    setSelectedPositionId(null);
    setNewStakingPeriod('');
  };

  const handleUnstakeAnytime = async (positionId) => {
    try {
      setIsProcessingAction(true);
      await unstakeTokens(positionId);
      setSnackbar({
        open: true,
        message: 'Successfully unstaked your tokens!',
        severity: 'warning',
      });
      await fetchStakingPositions();
    } catch (error) {
      if (error.code === 4001 ||
          error.code === 'ACTION_REJECTED' ||
          (error.message && error.message.toLowerCase().includes('user rejected')) ||
          (error.message && error.message.toLowerCase().includes('user denied')) ||
          (error.data && error.data.code === 'ACTION_REJECTED')) {
        return;
      } else {
        console.error('Unstaking failed:', error);
        setSnackbar({
          open: true,
          message: 'Failed to unstake tokens. Please try again.',
          severity: 'error',
        });
      }
    } finally {
      setIsProcessingAction(false);
    }
  };

  const handleViewOnExplorer = (positionId) => {
    if (positionId) {
      window.open(`${NETWORK_CONFIG.blockExplorer}/tx/${positionId}`, '_blank');
    }
  };

  return (
    <Box className="staking-positions-container" sx={{ padding: '20px' }}>
      {(loadingStake || loadingUnstake) ? (
        <Box display="flex" justifyContent="center" alignItems="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <ul className="staking-positions-list" style={{ listStyleType: 'none', padding: 0 }}>
          {stakingPositions.map((position) => {
            const isExpired = dayjs().isAfter(dayjs.unix(position.stakingEndTimestamp));
            const canClaimRewards = !position.rewardsClaimed && isExpired;
            const durationLabel = stakingPeriodLabels[position.stakingPeriod] || 'N/A';
            const formattedAmount = formatNumber(position.amount);
            const startDate = dayjs.unix(position.stakingStartTimestamp).format('D/M/YY');
            const endDate = dayjs.unix(position.stakingEndTimestamp).format('D/M/YY');

            return (
              <li 
                key={`${position.positionId}-${position.stakingStartTimestamp}-${position.amount}`} 
                className="staking-position"
              >
                <Button
                  onClick={() => toggleExpandPosition(position.positionId)}
                  style={{
                    justifyContent: 'flex-start',
                    padding: '10px',
                    width: '100%',
                    textAlign: 'left',
                    backgroundColor: '#1c1c1c',
                    color: '#ffcc00',
                  }}
                  disabled={isProcessingAction}
                >
                  {expandedPositionId === position.positionId ? '−' : '+'}
                  {`${formattedAmount} $CASH - ${durationLabel} - ${startDate}`}
                  {canClaimRewards && (
                    <CheckCircle style={{ color: 'green', fontSize: '1rem', marginLeft: '10px' }} />
                  )}
                </Button>
                <Collapse in={expandedPositionId === position.positionId} timeout="auto" unmountOnExit>
                  <Box sx={{ padding: '10px', border: '1px solid #ccc', borderRadius: '8px', marginTop: '10px' }}>
                    <Typography variant="body1">
                      <strong>Period:</strong> {durationLabel}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Starts On:</strong> {startDate}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Ends On:</strong> {endDate}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Status:</strong> {position.rewardsClaimed ? 'Rewards Claimed' : isExpired ? 'Expired' : 'Active'}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: '10px', marginTop: '10px', justifyContent: 'center' }}>
                      {canClaimRewards && (
                        <Button
                          onClick={() => handleClaimRewardClick(position.positionId)}
                          variant="contained"
                          color="primary"
                          disabled={isProcessingAction}
                          className="action-button"
                        >
                          {isProcessingAction && selectedPositionId === position.positionId ? <CircularProgress size={24} color="inherit" /> : 'Claim Rewards'}
                        </Button>
                      )}
                      <Button
                        onClick={() => handleUnstakeAnytime(position.positionId)}
                        variant="outlined"
                        color="secondary"
                        disabled={isProcessingAction}
                        className="action-button"
                      >
                        {isProcessingAction ? <CircularProgress size={24} color="inherit" /> : 'Unstake Anytime'}
                      </Button>
                      <Button
                        onClick={() => handleViewOnExplorer(position.positionId)}
                        variant="outlined"
                        color="secondary"
                        className="action-button"
                      >
                        View on Explorer
                      </Button>
                    </Box>
                  </Box>
                </Collapse>
              </li>
            );
          })}
        </ul>
      )}

      <Dialog open={openClaimDialog} onClose={handleCancelClaim}>
        <DialogTitle>Claim Rewards and Action</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            You are about to claim your rewards and perform an action with your staked tokens.
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Select your desired action:
          </Typography>
          <FormControl fullWidth>
            <InputLabel>New Staking Period</InputLabel>
            <Select
              value={newStakingPeriod}
              onChange={(e) => setNewStakingPeriod(e.target.value)}
              label="New Staking Period"
              disabled={isRestaking}
            >
              {stakingPeriodLabels.map((label, index) => (
                <MenuItem key={index} value={index}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClaim} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={() => handleClaimAndAction('restake')}
            color="primary"
            disabled={isProcessingAction || !newStakingPeriod}
          >
            {isRestaking ? <CircularProgress size={24} color="inherit" /> : 'Claim & Restake'}
          </Button>
          <Button
            onClick={() => handleClaimAndAction('unstake')}
            color="secondary"
            disabled={isProcessingAction}
          >
            {isProcessingAction ? <CircularProgress size={24} color="inherit" /> : 'Claim & Unstake'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StakingPositions;